import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Loader2, Save, Shield, Users } from "lucide-react";
import { Button } from "../../components/ui/button";

const API_URL = process.env.REACT_APP_API_URL;

const ALL_MENU_ITEMS = [
  { key: "/dashboard",                  label: "Dashboard",             group: "Principal" },
  { key: "/patients",                   label: "Pacientes",             group: "Principal" },
  { key: "/orders",                     label: "Órdenes",               group: "Principal" },
  { key: "/results",                    label: "Resultados",            group: "Principal" },
  { key: "/worklist",                   label: "Lista de trabajo",      group: "Principal" },
  { key: "/indicadores",                label: "Indicadores",           group: "Administración" },
  { key: "/params",                     label: "Valores de referencia", group: "Administración" },
  { key: "/tarifas-examenes",           label: "Tarifas exámenes",      group: "Administración" },
  { key: "/invoices",                   label: "Facturas",              group: "Administración" },
  { key: "/config",                     label: "Configuración",         group: "Administración" },
  { key: "/caja",                       label: "Caja",                  group: "Administración" },
  { key: "/caja/historico",             label: "Caja - Histórico",      group: "Administración" },
  { key: "/caja/pagos",                 label: "Caja - Pagos",          group: "Administración" },
  { key: "/caja/movimientos",           label: "Caja - Movimientos",    group: "Administración" },
  { key: "/config/clientes",            label: "Clientes",              group: "Configuración" },
  { key: "/config/especies",            label: "Especies",              group: "Configuración" },
  { key: "/config/razas",               label: "Razas",                 group: "Configuración" },
  { key: "/config/equipos",             label: "Equipos",               group: "Configuración" },
  { key: "/config/examenes",            label: "Exámenes",              group: "Configuración" },
  { key: "/config/caratulas_especie",   label: "Carátulas especie",     group: "Configuración" },
  { key: "/config/medicos",             label: "Médicos",               group: "Configuración" },
  { key: "/config/whatsapp",            label: "WhatsApp",              group: "Configuración" },
  { key: "/config/usuarios",            label: "Usuarios",              group: "Configuración" },
  { key: "/config/caja_categorias",     label: "Categorías de caja",    group: "Configuración" },
];

const GROUPS = [...new Set(ALL_MENU_ITEMS.map((m) => m.group))];

const UsuariosPage = () => {
  const token = localStorage.getItem("token");
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permisos, setPermisos] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/usuarios`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUsuarios(r.data || []))
      .catch(() => toast.error("Error al cargar usuarios"));
  }, [token]);

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/usuarios/${user.usr_codigo}/permisos`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const map = {};
      ALL_MENU_ITEMS.forEach((m) => { map[m.key] = false; });
      data.forEach((p) => { map[p.menu_key] = !!p.tiene_acceso; });
      setPermisos(map);
    } catch {
      toast.error("Error al cargar permisos");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) =>
    setPermisos((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleToggleAll = (group) => {
    const keys = ALL_MENU_ITEMS.filter((m) => m.group === group).map((m) => m.key);
    const allOn = keys.every((k) => permisos[k]);
    setPermisos((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { next[k] = !allOn; });
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const payload = ALL_MENU_ITEMS.map((m) => ({
        menu_key: m.key,
        menu_label: m.label,
        tiene_acceso: !!permisos[m.key],
      }));
      await axios.put(
        `${API_URL}/usuarios/${selectedUser.usr_codigo}/permisos`,
        { permisos: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Permisos actualizados correctamente");
    } catch {
      toast.error("Error al guardar permisos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {/* Lista de usuarios */}
      <div className="md:col-span-1">
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-brand-100 bg-brand-50">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-600" />
              Usuarios del sistema
            </h3>
          </div>
          <div className="divide-y divide-brand-50 max-h-[28rem] overflow-y-auto">
            {usuarios.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">
                No hay usuarios registrados
              </p>
            ) : (
              usuarios.map((u) => (
                <button
                  key={u.usr_codigo}
                  onClick={() => handleSelectUser(u)}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    selectedUser?.usr_codigo === u.usr_codigo
                      ? "bg-brand-50 border-l-4 border-brand-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <p className="font-semibold text-gray-800">{u.usr_codigo}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {u.nombre_completo || "—"}
                  </p>
                  {!u.activo && (
                    <span className="text-xs text-red-400 mt-0.5 block">
                      Inactivo
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Panel de permisos */}
      <div className="md:col-span-2">
        {!selectedUser ? (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-10 text-center">
            <Shield className="h-12 w-12 mx-auto mb-3 text-brand-200" />
            <p className="text-sm text-gray-400">
              Selecciona un usuario para gestionar sus permisos de menú
            </p>
          </div>
        ) : loading ? (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-10 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-brand-400" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
            <div className="px-4 py-3 border-b border-brand-100 bg-brand-50 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Shield className="h-4 w-4 text-brand-600" />
                Permisos —{" "}
                <span className="text-brand-700">{selectedUser.usr_codigo}</span>
                <span className="text-gray-400 font-normal">
                  ({selectedUser.nombre_completo})
                </span>
              </h3>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-1.5 text-xs"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Guardar cambios
              </Button>
            </div>

            <div className="p-5 space-y-5">
              {GROUPS.map((group) => {
                const groupItems = ALL_MENU_ITEMS.filter(
                  (m) => m.group === group,
                );
                const allOn = groupItems.every((m) => permisos[m.key]);
                return (
                  <div key={group}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                        {group}
                      </p>
                      <button
                        onClick={() => handleToggleAll(group)}
                        className="text-xs text-brand-600 hover:text-brand-800 transition-colors"
                      >
                        {allOn ? "Quitar todos" : "Seleccionar todos"}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {groupItems.map((m) => (
                        <label
                          key={m.key}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors select-none ${
                            permisos[m.key]
                              ? "border-brand-300 bg-brand-50"
                              : "border-brand-100 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!permisos[m.key]}
                            onChange={() => handleToggle(m.key)}
                            className="w-4 h-4 rounded accent-brand-600 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">
                            {m.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsuariosPage;
